import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { ErrorCode, purchaseErrorListener } from 'utils/packages/iap';
import Modal from 'utils/packages/Modal';
import Config from 'utils/packages/configs';
import usePurchasePreviewModal from 'features/promotions/hooks/usePurchasePreviewModal';
import { useEventBusEffect } from 'hooks/eventBus';
import { selectCurrentScreen, selectPreviousScreen } from 'reducers/selectors';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import { delay } from 'utils/delay';
import Sentry from 'utils/sentry';
import { setError } from 'actions';
import { isRunningTestflight, isWeb } from '../../../constants';
import { IAP_PURCHASE_FLOW_ENDED_WITH_SUCCESS, iapPurchaseFlowEndedWithError, iapPurchaseFlowEndedWithSuccess, setIAPPurchaseFlowStarted, } from '../actions';
import PurchaseErrorModalContent from '../components/PurchaseErrorModalContent';
import { TestflightInfoModal } from '../components/TestflightInfoModal';
import { TierPeriod, } from '../entities';
import { getActiveIapPurchase, requestSubscription } from '../iap';
import { selectAvailableIAPSubscriptionsWithPrices, selectShouldShowStripe, } from '../selectors';
import useLoadIapSubscriptions from './useLoadIapSubscriptions';
import useProcessIapPurchase, { resetProcessedPurchases, } from './useProcessIapPurchase';
import useStripePayment from './useStripePayment';
const isApplePlatform = Platform.OS === 'ios';
const toPurchaseError = (err, productId) => {
    if (typeof err === 'object' &&
        err !== null &&
        typeof err.code === 'string') {
        return err;
    }
    const fallback = new Error(err instanceof Error ? err.message : 'Failed to request the purchase');
    fallback.code = ErrorCode.PurchaseError;
    fallback.productId = productId;
    return fallback;
};
const noPurchaseError = (productId) => {
    const error = new Error('Your subscription wasn’t changed. Visit the app store to manage your subscription.');
    error.code = ErrorCode.PurchaseError;
    error.productId = productId;
    return error;
};
export const useAvailableIapSubscriptions = () => {
    const loadIapSubscriptions = useLoadIapSubscriptions();
    const shouldShowStripe = useAppSelector(selectShouldShowStripe);
    useEffect(() => {
        // This prevents crash if the first screen to load is Upgrade screen
        delay(() => {
            if (!shouldShowStripe) {
                loadIapSubscriptions();
            }
        }, Platform.OS === 'ios' ? 200 : 0);
    }, [loadIapSubscriptions, shouldShowStripe]);
    return useAppSelector(selectAvailableIAPSubscriptionsWithPrices);
};
export const usePurchase = (
/**
 * Returned after the purchase has completed
 * @param productId The id returned from the purchase
 * @param newSku The product that the user actually tried to purchase, these may be different if the user has an active subscription and tries to change the period (the original product id will still be returned for productId)
 */
doOnPurchase, analyticsFlow, benefitId, 
/**
 * Skips the Stripe purchase preview modal and charges straight away. Only for
 * flows where there is nothing to preview, e.g. an offer that takes the whole
 * of the next payment off.
 */
{ skipPreview, previewVariant, isRefundFlow, } = {}) => {
    const { startStripeFlow, fetching: isFetchingStripe, canUseStripe, } = useStripePayment();
    const currentSubscription = useRef(undefined);
    const emmaProStatus = useAppSelector((store) => store.user.user.emmaProStatus);
    const [isFetching, setIsFetching] = useState(false);
    const isIAPPurchasing = useAppSelector((store) => store.iapSubscriptions.isPurchasingSubscription);
    const dispatch = useAppDispatch();
    const { openPurchasePreviewModal, isFetchingStripePreview } = usePurchasePreviewModal();
    /**
     * Whether this instance started the IAP flow that is currently in progress.
     *
     * `IAP_PURCHASE_FLOW_ENDED_WITH_SUCCESS` is a global event bus event, so
     * every mounted `usePurchase` observes it
     */
    const didStartPurchaseFlow = useRef(false);
    const handleDoOnPurchase = useCallback((productId, newSku, discount, proratedRefund) => {
        didStartPurchaseFlow.current = false;
        doOnPurchase?.(productId, newSku, discount, proratedRefund);
        dispatch(iapPurchaseFlowEndedWithSuccess());
    }, [doOnPurchase, dispatch]);
    const subscribeRef = useRef(undefined);
    const onPurchaseError = useCallback((error) => {
        setIsFetching(false);
        didStartPurchaseFlow.current = false;
        dispatch(iapPurchaseFlowEndedWithError());
        const isSilent = error.code === ErrorCode.UserCancelled ||
            error.code === ErrorCode.ServiceDisconnected ||
            error.code === ErrorCode.DeferredPayment;
        if (currentSubscription.current && !isSilent) {
            const curr = currentSubscription.current;
            Modal.show(<PurchaseErrorModalContent error={error} onPressTryAgain={curr
                    ? () => {
                        Modal.hide(() => {
                            subscribeRef.current?.(curr.subscription, curr?.discount);
                        });
                    }
                    : undefined}/>, {
                title: 'Error',
            });
            if (error.code !== ErrorCode.UserError &&
                error.code !== ErrorCode.NetworkError &&
                error.code !== ErrorCode.ServiceError &&
                error.code !== ErrorCode.ServiceDisconnected) {
                // https://adapty.io/blog/ios-in-app-purchases-part-5-list-of-skerror-codes-and-how-to-handle-them
                Sentry.logError({
                    fingerprint: ['usePurchase', 'initConnection'],
                    message: error.message,
                    data: error,
                });
            }
        }
    }, [dispatch]);
    const processPurchase = useProcessIapPurchase();
    const completeApplePurchase = useCallback(async (result, requestedProductId) => {
        const purchase = Array.isArray(result) ? result[0] : result ?? undefined;
        if (purchase) {
            await processPurchase(purchase);
            return;
        }
        const active = await getActiveIapPurchase(requestedProductId);
        if (active) {
            await processPurchase(active);
            return;
        }
        onPurchaseError(noPurchaseError(requestedProductId));
    }, [onPurchaseError, processPurchase]);
    const store = useAppStore();
    const subscribe = useCallback(async (subscription, discount, skipFreeTrial) => {
        if (isRunningTestflight && Config?.IS_PROD !== 'false' && !canUseStripe) {
            TestflightInfoModal.show();
            return;
        }
        const handleError = (err) => {
            if (err instanceof Error &&
                subscription.platform === 'stripe' &&
                err.message !== 'The payment has been canceled' &&
                err.message !== 'Google Pay has been canceled' &&
                err.message !== 'The payment flow has been canceled') {
                Sentry.logError({ err, fingerprint: ['usePurchase'] });
                dispatch(setError('Error', err.message));
            }
        };
        currentSubscription.current = { subscription, discount };
        didStartPurchaseFlow.current = true;
        setIsFetching(true);
        const analytics = {
            analyticsFlow,
            benefitId,
            currentScreen: selectCurrentScreen(store.getState()) || undefined,
            previousScreen: selectPreviousScreen(store.getState()),
        };
        try {
            if (subscription.platform !== 'stripe') {
                if (discount && subscription.platform !== discount.type)
                    return;
                dispatch(setIAPPurchaseFlowStarted({
                    subscription,
                    discount,
                    analytics,
                }));
                resetProcessedPurchases();
                try {
                    const result = await requestSubscription(subscription, emmaProStatus, discount);
                    if (isApplePlatform) {
                        await completeApplePurchase(result, subscription.id);
                    }
                }
                catch (err) {
                    onPurchaseError(toPurchaseError(err, subscription.id));
                }
            }
            else {
                if (discount && discount.type !== subscription.platform)
                    return;
                if (skipPreview) {
                    const isSubscriptionChange = emmaProStatus?.active && emmaProStatus.store === 'stripe';
                    await startStripeFlow({
                        doOnPurchase: handleDoOnPurchase,
                        analytics,
                        subscription,
                        emmaProStatus,
                        discount,
                        prorationDate: isSubscriptionChange
                            ? new Date().toISOString()
                            : undefined,
                        skipFreeTrial,
                        isRefundFlow,
                    });
                    Modal.hide();
                    return;
                }
                await openPurchasePreviewModal({
                    subscription,
                    discount,
                    formattedDiscountPrices: discount?.formattedDiscountPrices,
                    skipFreeTrial,
                    variant: previewVariant,
                    isRefundFlow,
                    onPressContinue: async (payToday, prorationDate) => {
                        try {
                            await startStripeFlow({
                                doOnPurchase: handleDoOnPurchase,
                                analytics,
                                subscription,
                                emmaProStatus,
                                discount,
                                payToday,
                                prorationDate,
                                skipFreeTrial,
                                isRefundFlow,
                            });
                            Modal.hide();
                        }
                        catch (err) {
                            handleError(err);
                        }
                    },
                });
            }
        }
        catch (err) {
            handleError(err);
        }
        finally {
            setIsFetching(false);
        }
    }, [
        canUseStripe,
        emmaProStatus,
        analyticsFlow,
        benefitId,
        openPurchasePreviewModal,
        previewVariant,
        isRefundFlow,
        skipPreview,
        startStripeFlow,
        handleDoOnPurchase,
        completeApplePurchase,
        onPurchaseError,
    ]);
    useEffect(() => {
        subscribeRef.current = subscribe;
    }, [subscribe]);
    useEffect(() => {
        if (isWeb)
            return () => { };
        const errorListener = purchaseErrorListener(onPurchaseError);
        return () => {
            errorListener?.remove();
        };
    }, [onPurchaseError]);
    useEventBusEffect((res) => {
        if (!didStartPurchaseFlow.current)
            return;
        didStartPurchaseFlow.current = false;
        if (res?.newSku !== undefined) {
            handleDoOnPurchase?.(res.productId, res.newSku);
        }
    }, [IAP_PURCHASE_FLOW_ENDED_WITH_SUCCESS]);
    return [
        isFetching ||
            Boolean(isIAPPurchasing) ||
            isFetchingStripePreview ||
            isFetchingStripe,
        subscribe,
    ];
};
export const getTierSubscriptions = (subscriptions, tier, canUseStripe, stripePriceMap) => {
    if (canUseStripe) {
        if (stripePriceMap) {
            return [
                stripePriceMap[TierPeriod.yearly][tier],
                stripePriceMap[TierPeriod.monthly][tier],
            ];
        }
        return [];
    }
    const periodsOrder = Object.values(TierPeriod).reduce((prev, curr, index) => {
        const result = { ...prev };
        result[curr] = index;
        return result;
    }, {});
    return subscriptions
        .filter((it) => it.tier === tier)
        .sort((a, b) => periodsOrder[b.period] - periodsOrder[a.period]);
};
export const getYearlyTierSubscriptions = (subscriptions, tiers, canUseStripe, stripePriceMap) => {
    if (canUseStripe) {
        if (stripePriceMap) {
            return tiers.map((tier) => stripePriceMap[TierPeriod.yearly][tier]);
        }
        return [];
    }
    return tiers
        .map((tier) => subscriptions.find((sub) => sub.tier === tier && sub.period === TierPeriod.yearly))
        .filter((sub) => sub !== undefined);
};
